/**
 * PlaceAdminOrderForm — agent-facing form to create an order on
 * exoticindia.com via the admin_crm.php API.
 *
 * Lives in the chat right-panel "order" tab. Pre-fills from the lead,
 * supports AI auto-fill from chat (LLM extracts name/address/items/txn
 * + OCRs payment screenshots), and surfaces an inline price summary
 * pulled from the catalog. On submit, returns the website's orderid.
 *
 * Extracted from `WebsiteOrdersPanel.jsx` during the Feb 2026
 * code-quality split.
 */
import { useState, useEffect, useMemo } from "react";
import {
  Loader2, Package, Plus, X, Send, Check, ExternalLink, Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import { websiteOrdersApi, booksApi } from "../../lib/api";
import {
  PAYMENT_TYPES, CURRENCIES, INPUT_CLASS, Field,
} from "./websiteOrdersUtils";

/**
 * PrefillMetaPills — sources + confidence indicator after the agent clicks
 * "Auto-fill from chat".
 *
 * The transparency lets the agent know which fields are webhook-verified
 * (high trust — green) vs. AI-extracted (verify before placing — amber).
 *
 * @example webhook-paid lead:
 *   txn_id ✅ Razorpay (verified)   address ⚠ AI (90%)   items ✅ chat URLs
 */
const PrefillMetaPills = ({ meta }) => {
  // Iter 156.22 — pill array memoised. The `useMemo` MUST run before any
  // early return to keep Rules-of-Hooks happy; the inner `if (!meta)` guard
  // short-circuits to an empty array when there's nothing to display.
  // (Supersedes the 156.5 "not worth memoising" note — user explicitly
  // requested the optimisation, restructure is hooks-safe.)
  const pills = useMemo(() => {
    if (!meta) return [];
    const { sources = {}, confidence = {}, webhook_amount, ocr_amount } = meta;

    const txnPill = (() => {
      const s = sources.txn_id;
      if (!s) return { tone: "muted", label: "Txn: not found", title: "Couldn't find a payment in chat or webhooks." };
      if (s === "razorpay_webhook") return { tone: "green", label: "Txn ✅ Razorpay webhook", title: "Verified via Razorpay webhook — trust this." };
      if (s === "stripe_webhook") return { tone: "green", label: "Txn ✅ Stripe webhook", title: "Verified via Stripe webhook — trust this." };
      if (s === "paypal_webhook") return { tone: "green", label: "Txn ✅ PayPal webhook", title: "Verified via PayPal webhook — trust this." };
      if (s === "screenshot_ocr") return { tone: "amber", label: "Txn ⚠ Screenshot OCR", title: "Extracted from a payment screenshot via Claude Vision — verify before placing." };
      return { tone: "muted", label: `Txn: ${s}`, title: s };
    })();

    const itemsPill = (() => {
      const s = sources.items;
      if (s === "chat_urls") return { tone: "green", label: "Items ✅ chat URLs", title: "Item codes extracted from product URLs the customer shared." };
      if (s === "ai_extracted") return { tone: "amber", label: `Items ⚠ AI (${confidence.items || 0}%)`, title: "Inferred by AI from chat text — verify the codes." };
      return { tone: "muted", label: "Items: empty", title: "No items detected — add manually." };
    })();

    const addrPill = sources.address === "ai_extracted"
      ? { tone: confidence.address >= 80 ? "green" : "amber", label: `Address ${confidence.address >= 80 ? "✅" : "⚠"} AI (${confidence.address}%)`, title: confidence.address >= 80 ? "High AI confidence — quick scan and proceed." : "Lower AI confidence — verify carefully." }
      : { tone: "muted", label: "Address: not found", title: "No shipping address detected — please ask the customer." };

    const amountPill = (() => {
      if (webhook_amount) return { tone: "green", label: `Amount ✅ ${webhook_amount}`, title: "Amount confirmed by payment-gateway webhook." };
      if (ocr_amount) return { tone: "amber", label: `Amount ⚠ ${ocr_amount} (OCR)`, title: "Amount read from payment screenshot — verify with the customer." };
      return null;
    })();

    return [txnPill, amountPill, itemsPill, addrPill].filter(Boolean);
  }, [meta]);

  if (!meta) return null;
  const { ocr_count = 0 } = meta;

  return (
    <div className="rounded-xl border border-violet-200 bg-violet-50 p-2.5 space-y-1.5" data-testid="prefill-meta-pills">
      <div className="flex items-center justify-between">
        <p className="text-[9px] font-extrabold uppercase tracking-wider text-violet-700">
          AI prefill summary
        </p>
        {ocr_count > 0 && (
          <span className="text-[9px] font-bold text-violet-600">
            OCR'd {ocr_count} image{ocr_count === 1 ? "" : "s"}
          </span>
        )}
      </div>
      <div className="flex flex-wrap gap-1">
        {pills.map((p, i) => (
          <span
            key={p.label}
            title={p.title}
            className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full border ${
              p.tone === "green" ? "bg-emerald-50 text-emerald-700 border-emerald-200"
              : p.tone === "amber" ? "bg-amber-50 text-amber-700 border-amber-200"
              : "bg-slate-100 text-slate-600 border-slate-200"
            }`}
            data-testid={`prefill-pill-${i}`}
          >
            {p.label}
          </span>
        ))}
      </div>
      <p className="text-[9px] text-violet-600 italic">
        Verify highlighted fields below, then press <span className="font-bold">Place Admin Order</span>.
      </p>
    </div>
  );
};

export const PlaceAdminOrderForm = ({ leadId, lead, onCreated }) => {
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [createdOrderId, setCreatedOrderId] = useState(null);
  const [form, setForm] = useState({
    accessing_country: "IN",
    currency: "INR",
    first_name: "",
    last_name: "",
    company: "",
    address_line1: "",
    address_line2: "",
    city: "",
    state: "",
    zipcode: "",
    country: "IN",
    mobile: "",
    email: "",
    gstin: "",
    transid: "",
    paymenttype: "CRM",
    narration: "",
    coupon: "",
    // Iter 148 (Feb 2026) — Yash asked for COD charges + custom discount
    // (flat rupees off the final total, independent of the coupon engine).
    // Both are OPTIONAL on the upstream API and are forwarded only when
    // non-empty.
    codcharges: "",
    custom_reduce: "",
    auto_coupon_type: "",        // "" | "d" | "c"
    auto_coupon_value: "",       // numeric string
    // Shipping defaults to billing
    shipping_first_name: "",
    shipping_last_name: "",
    shipping_address_line1: "",
    shipping_city: "",
    shipping_state: "",
    shipping_zipcode: "",
    shipping_country: "IN",
    shipping_mobile: "",
    shipping_email: "",
  });
  const [products, setProducts] = useState([{ itemcode: "", qty: 1, size: "", color: "", options: "" }]);
  const [shipDifferent, setShipDifferent] = useState(false);
  // Yash, Apr 29 2026 — AI prefill state. `prefillMeta` holds the per-field
  // sources + confidence returned by /prefill-from-chat so the UI can show
  // green pills next to webhook-verified fields and amber for AI-extracted
  // ones. Populated only after the agent clicks "Auto-fill from chat".
  const [prefilling, setPrefilling] = useState(false);
  const [prefillMeta, setPrefillMeta] = useState(null); // { sources, confidence, ocr_count }
  // Yash, Apr 29 2026 — auto-fill price from catalog using simple rules:
  //   • currency=INR → use product's INR price (selling_inr)
  //   • currency=anything else (USD/GBP/EUR/...) → use USD price (selling_usd)
  // No FX conversion. Mirrors the existing AI prompt rules: India sees INR,
  // overseas sees USD only. Cached per itemcode so retyping doesn't refetch.
  const [catalogPrices, setCatalogPrices] = useState({}); // { ZJ40: { inr: 1750, usd: 22 } }
  const [pricesLoading, setPricesLoading] = useState({});

  // Pre-fill from lead when the form opens
  useEffect(() => {
    if (!open || !lead) return;
    const phone = (lead.customer_phone || lead.phone || "").replace(/\D+/g, "");
    const email = (lead.customer_email || "").trim();
    const fullName = (lead.customer_name || lead.name || "").trim();
    const [first, ...rest] = fullName.split(/\s+/);
    setForm((f) => ({
      ...f,
      first_name: f.first_name || first || "",
      last_name: f.last_name || rest.join(" ") || "",
      mobile: f.mobile || phone,
      email: f.email || email,
      country: f.country || (lead.country || "IN").toUpperCase().slice(0, 2),
      accessing_country: f.accessing_country || (lead.country || "IN").toUpperCase().slice(0, 2),
    }));
  }, [open, lead]);

  // Catalog price lookup — debounced 350ms after the agent stops typing
  // an itemcode. Iter 153.19 (Yash, Feb 8 2026) — also caches FX-derived
  // prices for AED/AUD/SGD/CAD/etc. so flipping currency on an overseas
  // order shows the right number instantly. Previously we only stored
  // INR + USD and `useUsd ? cp.usd : cp.inr` silently quoted the
  // US-ship-to-US selling_usd for ALL non-INR currencies (UAE, UK, EU,
  // AU, …) — those customers were getting the wrong price.
  useEffect(() => {
    if (!open) return;
    const codes = Array.from(new Set(
      products.map((p) => (p.itemcode || "").trim().toUpperCase()).filter(Boolean)
    ));
    const toFetch = codes.filter((c) => !(c in catalogPrices) && !pricesLoading[c]);
    if (!toFetch.length) return;
    const t = setTimeout(() => {
      toFetch.forEach(async (code) => {
        setPricesLoading((s) => ({ ...s, [code]: true }));
        try {
          const r = await booksApi.lookup(code);
          const pr = r.data?.pricing_resolved || {};
          // fx_prices.currencies is a dict {AED: {amount, unavailable}, AUD: ..., ...}.
          // Iter 153.19 — flatten into a `fx[CUR] = numericPrice` map; preserve
          // `unavailable` markers (GBP/EUR are pending VAT) so the UI can show
          // "Quote via Sales" instead of a wrong number.
          const fxRaw = r.data?.fx_prices?.currencies || {};
          const fx = {};
          const fxUnavailable = new Set();
          for (const [cur, info] of Object.entries(fxRaw)) {
            if (info?.unavailable) {
              fxUnavailable.add(cur);
            } else if (info?.amount != null) {
              fx[cur.toUpperCase()] = Number(info.amount);
            }
          }
          setCatalogPrices((s) => ({
            ...s,
            [code]: {
              inr: pr.selling_inr ?? pr.mrp_inr ?? null,
              usd: pr.selling_usd ?? pr.mrp_usd ?? null,
              fx,
              fxUnavailable: Array.from(fxUnavailable),
            },
          }));
        } catch (_) {
          setCatalogPrices((s) => ({ ...s, [code]: { inr: null, usd: null, missing: true } }));
        } finally {
          setPricesLoading((s) => { const n = { ...s }; delete n[code]; return n; });
        }
      });
    }, 350);
    return () => clearTimeout(t);
  }, [open, products, catalogPrices, pricesLoading]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const addProduct = () => setProducts((p) => [...p, { itemcode: "", qty: 1, size: "", color: "", options: "" }]);
  const removeProduct = (i) => setProducts((p) => p.length > 1 ? p.filter((_, idx) => idx !== i) : p);
  const updateProduct = (i, k, v) => setProducts((p) => p.map((row, idx) => idx === i ? { ...row, [k]: v } : row));

  // Iter 153.19 (Yash, Feb 8 2026) — currency-aware price lookup.
  //   • INR  → use cp.inr (selling_inr, India catalog)
  //   • USD  → use cp.usd (selling_usd, US-ship-to-US tariff-inclusive)
  //   • AED/AUD/SGD/CAD/CHF/JPY/etc. → use cp.fx[CUR] (FX-converted from
  //     selling_base_usd at current rates — see _compute_fx_prices in
  //     server.py:803). This was the bug for lead 971507504918 (UAE/AED)
  //     where currency switched to AED but price stayed at the US-only
  //     selling_usd figure.
  //   • GBP/EUR/etc. flagged unavailable in fx_prices → "Quote via Sales"
  //     (those need VAT pricing logic that's still pending).
  const formCurrency = (form.currency || "INR").toUpperCase();
  const priceFieldLabel = formCurrency;
  const curSymbol = (
    formCurrency === "INR" ? "₹" :
    formCurrency === "USD" ? "$" :
    formCurrency === "GBP" ? "£" :
    formCurrency === "EUR" ? "€" :
    formCurrency === "JPY" ? "¥" :
    formCurrency + " "  // AED 245, AUD 90, etc.
  );
  const linePrices = products.map((p) => {
    const code = (p.itemcode || "").trim().toUpperCase();
    if (!code) return { unit: null, total: null };
    if (pricesLoading[code]) return { loading: true };
    const cp = catalogPrices[code];
    if (!cp) return { unit: null, total: null };
    if (cp.missing) return { missing: true };
    let unit = null;
    if (formCurrency === "INR") unit = cp.inr;
    else if (formCurrency === "USD") unit = cp.usd;
    else if (cp.fx && cp.fx[formCurrency] != null) unit = cp.fx[formCurrency];
    else if (cp.fxUnavailable && cp.fxUnavailable.includes(formCurrency)) {
      return { unavailableForCur: true };
    }
    if (unit == null) return { missingForCur: true };
    const qty = Number(p.qty) || 0;
    return { unit, total: unit * qty };
  });
  const subtotal = linePrices.reduce(
    (acc, lp) => (acc == null || lp.total == null ? null : acc + lp.total),
    0
  );
  // Apply the negotiated discount in the SAME way the upstream PHP does:
  //   • type "d" → subtract `value`% of subtotal
  //   • type "c" → subtract a flat amount in the form's currency
  // The form total must reflect what the customer actually pays so the
  // agent can sanity-check before pressing "Place admin order".
  const discType = form.auto_coupon_type || "";
  const discValueRaw = parseFloat(form.auto_coupon_value);
  const discValue = isNaN(discValueRaw) ? 0 : discValueRaw;
  let discountAmount = 0;
  if (subtotal != null && discValue > 0) {
    if (discType === "d") discountAmount = subtotal * (discValue / 100);
    else if (discType === "c") discountAmount = discValue;
  }
  const grandTotal = subtotal == null ? null : Math.max(0, subtotal - discountAmount);

  // Yash, Apr 29 2026 — AI prefill from chat. Spreads the returned shape
  // straight into form state, replaces the products list with detected items,
  // and surfaces per-field confidence so the agent knows what to verify.
  // Iter 153.18 — normalize the LLM-extracted payment_mode to match the
  // PAYMENT_TYPES dropdown values. The LLM returns title-case ("COD",
  // "UPI", "Card", "Net Banking", "Bank Transfer"); the form's <select>
  // options are lowercase / kebab-case ("cod", "upi", "bank-transfer",
  // ...). Without this, a clearly-COD chat would silently fall back to
  // the form's default value because the select couldn't match.
  const _normalizePaymentMode = (raw) => {
    if (!raw) return null;
    const s = String(raw).trim().toLowerCase();
    if (!s) return null;
    if (["cod", "cash on delivery", "cash"].includes(s)) return "cod";
    if (["upi", "gpay", "phonepe", "paytm"].includes(s)) return "upi";
    if (["bank transfer", "bank-transfer", "neft", "rtgs", "imps", "wire"].includes(s)) return "bank-transfer";
    if (s === "razorpay") return "razorpay";
    if (s === "stripe") return "stripe";
    if (s === "paypal") return "paypal";
    if (["card", "credit card", "debit card", "net banking", "netbanking"].includes(s)) return "other";
    if (["special pay", "proforma"].includes(s)) return "other";
    if (s === "crm") return "CRM";
    return null;  // unknown — keep form's existing value
  };

  const prefillFromChat = async () => {
    if (!leadId) {
      toast.error("Open a lead first to auto-fill.");
      return;
    }
    setPrefilling(true);
    try {
      const res = await websiteOrdersApi.prefillFromChat(leadId);
      const pf = res.data?.prefill || {};
      // Iter 153.20 (Feb 8 2026, Yash) — was using bare `f.paymenttype` which
      // is undefined outside the setForm callback. Use `form` (state) so the
      // fallback resolves correctly when LLM extraction returns an unknown
      // payment phrase. (Real-world impact was rare: only triggered when
      // _normalizePaymentMode returned null — but the ReferenceError would
      // silently abort the whole prefill, dropping every field.)
      const normalisedPaymentType = _normalizePaymentMode(pf.paymenttype) || form.paymenttype;
      setForm((f) => ({
        ...f,
        first_name: pf.first_name || f.first_name,
        last_name: pf.last_name || f.last_name,
        company: pf.company || f.company,
        address_line1: pf.address_line1 || f.address_line1,
        address_line2: pf.address_line2 || f.address_line2,
        city: pf.city || f.city,
        state: pf.state || f.state,
        zipcode: pf.zipcode || f.zipcode,
        country: pf.country || f.country,
        accessing_country: pf.accessing_country || f.accessing_country,
        email: pf.email || f.email,
        mobile: pf.mobile || f.mobile,
        transid: pf.transid || f.transid,
        paymenttype: normalisedPaymentType,
        currency: pf.currency || f.currency,
        narration: pf.narration || f.narration,
        // Iter 153.18 (Feb 8, 2026, Yash) — auto-fill must populate
        // codcharges + custom_reduce too. Previously these stayed empty
        // even on clearly-COD chats, forcing manual entry every time.
        // Rule: if backend returned a numeric cod_charge, use it
        // verbatim. Else, if normalized paymenttype is "cod", auto-default
        // to ₹60 (matches the same default used by the auto-place flow at
        // server.py:20542). Empty for non-COD paymenttypes.
        codcharges: (
          pf.cod_charge != null && pf.cod_charge !== ""
            ? String(pf.cod_charge)
            : (normalisedPaymentType === "cod" ? "60" : f.codcharges)
        ),
        // Iter 153.18 — surface flat-rupee discount when AI extracted
        // one. discount_percent is converted to flat rupees server-side
        // during auto-place but the manual form takes flat rupees only.
        custom_reduce: (
          pf.discount_amount != null && pf.discount_amount !== ""
            ? String(Math.round(Number(pf.discount_amount)))
            : f.custom_reduce
        ),
      }));
      const items = Array.isArray(pf.products) ? pf.products : [];
      if (items.length > 0) {
        setProducts(items.map((it) => ({
          itemcode: (it.itemcode || "").toUpperCase(),
          qty: Number(it.qty) || 1,
          size: it.size || "",
          color: it.color || "",
        })));
      }
      setPrefillMeta({
        sources: res.data?.sources || {},
        confidence: res.data?.confidence || {},
        ocr_count: res.data?.ocr_count || 0,
        webhook_amount: res.data?.webhook_amount,
        ocr_amount: res.data?.ocr_amount,
      });
      toast.success(
        `Auto-filled from chat${res.data?.ocr_count ? ` · OCR'd ${res.data.ocr_count} payment image${res.data.ocr_count === 1 ? "" : "s"}` : ""}. Verify before placing.`,
        { duration: 6000 },
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || "AI prefill failed — fill manually.");
    } finally {
      setPrefilling(false);
    }
  };

  const submit = async () => {
    // Quick client-side validation — server validates the rest.
    const required = ["first_name", "last_name", "address_line1", "city", "state", "zipcode", "country", "mobile", "email"];
    for (const k of required) {
      if (!String(form[k] || "").trim()) {
        toast.error(`Missing required field: ${k.replace(/_/g, " ")}`);
        return;
      }
    }
    if (!products.length || !products[0].itemcode) {
      toast.error("Add at least one product with an itemcode");
      return;
    }

    const payload = {
      ...form,
      products: products
        .filter((p) => (p.itemcode || "").trim())
        .map((p) => ({
          itemcode: p.itemcode.trim().toUpperCase(),
          qty: Number(p.qty) || 1,
          size: (p.size || "").trim() || undefined,
          color: (p.color || "").trim() || undefined,
          // Iter 148 — pipe-separated cart_entry add-ons (see API schema).
          options: (p.options || "").trim() || undefined,
        })),
      lead_id: leadId,
    };
    // Iter 148 — codcharges + custom_reduce. Forward only when the agent
    // actually entered a positive integer, otherwise strip the keys so the
    // upstream doesn't see empty strings.
    if (String(form.codcharges || "").trim()) {
      const n = parseInt(form.codcharges, 10);
      if (!Number.isNaN(n) && n >= 0) payload.codcharges = n;
      else delete payload.codcharges;
    } else {
      delete payload.codcharges;
    }
    if (String(form.custom_reduce || "").trim()) {
      const n = parseInt(form.custom_reduce, 10);
      if (!Number.isNaN(n) && n >= 0) payload.custom_reduce = n;
      else delete payload.custom_reduce;
    } else {
      delete payload.custom_reduce;
    }
    // Auto-coupon — only forward if BOTH type and value are present.
    if (form.auto_coupon_type && form.auto_coupon_value) {
      payload.auto_coupon_type = form.auto_coupon_type;
      payload.auto_coupon_value = Number(form.auto_coupon_value);
    } else {
      delete payload.auto_coupon_type;
      delete payload.auto_coupon_value;
    }
    if (!shipDifferent) {
      delete payload.shipping_first_name;
      delete payload.shipping_last_name;
      delete payload.shipping_address_line1;
      delete payload.shipping_city;
      delete payload.shipping_state;
      delete payload.shipping_zipcode;
      delete payload.shipping_country;
      delete payload.shipping_mobile;
      delete payload.shipping_email;
    }

    setSubmitting(true);
    try {
      const res = await websiteOrdersApi.create(payload);
      const orderid = res.data?.orderid;
      const autoCouponCode = res.data?.auto_coupon_code;
      setCreatedOrderId(orderid);
      toast.success(
        autoCouponCode
          ? `Admin order #${orderid} placed with coupon ${autoCouponCode}`
          : `Admin order placed! #${orderid}`,
        { duration: 7000 },
      );
      onCreated?.(orderid);
    } catch (e) {
      // Iter 153.38 — OOS preflight on backend. When backend returns 409
      // out_of_stock_blocked, surface a confirm dialog and offer to retry
      // with `acknowledge_oos: true`. Lead 919313743422 — Sales had no
      // signal NZK565 was OOS before placing.
      const detail = e.response?.data?.detail;
      if (
        e.response?.status === 409 &&
        detail &&
        typeof detail === "object" &&
        detail.error === "out_of_stock_blocked"
      ) {
        const itemList = (detail.items || []).join("\n• ");
        const confirmed = window.confirm(
          `⚠️ OUT OF STOCK\n\nThese items are out of stock:\n• ${itemList}\n\n` +
          `We do NOT offer backorder on books or clothing.\n\n` +
          `Are you sure you've confirmed with the warehouse and want to ` +
          `proceed anyway?\n\n(Click OK to place anyway, Cancel to abort.)`
        );
        if (confirmed) {
          try {
            const retryRes = await websiteOrdersApi.create({
              ...payload,
              acknowledge_oos: true,
            });
            const orderid = retryRes.data?.orderid;
            const autoCouponCode = retryRes.data?.auto_coupon_code;
            setCreatedOrderId(orderid);
            toast.success(
              `Admin order placed (OOS override)! #${orderid}` +
                (autoCouponCode ? ` · coupon ${autoCouponCode}` : ""),
              { duration: 7000 },
            );
            onCreated?.(orderid);
          } catch (retryErr) {
            toast.error(
              retryErr.response?.data?.detail || "Failed to place admin order"
            );
          }
        } else {
          toast.error("Order cancelled — items are out of stock.");
        }
      } else {
        toast.error(detail || "Failed to place admin order");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="p-4" data-testid="place-admin-order-section">
      <div className="flex items-center justify-between mb-2.5">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg flex items-center justify-center bg-gradient-to-br from-emerald-500 to-teal-500 text-white shadow-sm">
            <Package size={13} />
          </div>
          <div>
            <p className="text-[12px] font-extrabold text-[--text-primary] leading-tight">Place Admin Order</p>
            <p className="text-[9px] text-[--text-muted] leading-tight">Creates an order on exoticindia.com</p>
          </div>
        </div>
        <button
          onClick={() => { setOpen((o) => !o); setCreatedOrderId(null); }}
          className="text-[10px] font-bold text-emerald-700 hover:text-emerald-900 flex items-center gap-1 px-2 py-1 rounded-lg hover:bg-emerald-50 transition-all"
          data-testid="toggle-place-admin-order-btn"
        >
          {open ? <><X size={11} /> Close form</> : <><Plus size={11} /> New order</>}
        </button>
      </div>

      {createdOrderId && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-2.5 mb-2 flex items-center gap-2" data-testid="admin-order-success">
          <Check size={14} className="text-emerald-600" />
          <div className="flex-1">
            <p className="text-[11px] font-bold text-emerald-800">Order #{createdOrderId} created</p>
            <p className="text-[9px] text-emerald-600">Saved to lead history. The customer can be informed.</p>
          </div>
          <a
            href={`https://www.exoticindia.com/admin/order/${createdOrderId}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-emerald-700 hover:text-emerald-900"
            title="Open on website"
            data-testid="admin-order-open-link"
          >
            <ExternalLink size={12} />
          </a>
        </div>
      )}

      {open && !createdOrderId && (
        <div className="space-y-2.5 bg-slate-50 border border-slate-200 rounded-xl p-3" data-testid="place-admin-order-form">
          {/* Yash, Apr 29 2026 — AI auto-fill button. Pulls name, address,
              items, txn id (Razorpay/Stripe/PayPal webhook OR OCR on
              payment screenshots), currency and a notes summary in one shot.
              Confidence badges below show what to trust vs verify. */}
          <button
            type="button"
            onClick={prefillFromChat}
            disabled={prefilling || !leadId}
            className="w-full flex items-center justify-center gap-2 py-2 rounded-xl bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white text-[11px] font-extrabold uppercase tracking-wider shadow-sm hover:shadow-md hover:from-violet-600 hover:to-fuchsia-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            data-testid="prefill-from-chat-btn"
          >
            {prefilling ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />}
            {prefilling ? "Reading chat & screenshots…" : "Auto-fill from chat (AI)"}
          </button>

          {prefillMeta && (
            <PrefillMetaPills meta={prefillMeta} />
          )}

          {/* Country / currency */}
          <div className="grid grid-cols-3 gap-2">
            <Field label="Access country" testid="form-accessing-country">
              <input value={form.accessing_country} onChange={(e) => setField("accessing_country", e.target.value.toUpperCase().slice(0, 2))}
                className={INPUT_CLASS} maxLength={2} placeholder="IN" data-testid="form-accessing-country-input" />
            </Field>
            <Field label="Currency" testid="form-currency">
              <select value={form.currency} onChange={(e) => setField("currency", e.target.value)} className={INPUT_CLASS} data-testid="form-currency-select">
                {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
            <Field label="Payment type" testid="form-paymenttype">
              <select value={form.paymenttype} onChange={(e) => setField("paymenttype", e.target.value)} className={INPUT_CLASS} data-testid="form-paymenttype-select">
                {PAYMENT_TYPES.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </Field>
          </div>

          {/* Customer name */}
          <div className="grid grid-cols-2 gap-2">
            <Field label="First name *">
              <input value={form.first_name} onChange={(e) => setField("first_name", e.target.value)} className={INPUT_CLASS} data-testid="form-first-name-input" />
            </Field>
            <Field label="Last name *">
              <input value={form.last_name} onChange={(e) => setField("last_name", e.target.value)} className={INPUT_CLASS} data-testid="form-last-name-input" />
            </Field>
          </div>

          {/* Contact */}
          <div className="grid grid-cols-2 gap-2">
            <Field label="Mobile *">
              <input value={form.mobile} onChange={(e) => setField("mobile", e.target.value)} className={INPUT_CLASS} placeholder="9198XXXXXXXX" data-testid="form-mobile-input" />
            </Field>
            <Field label="Email *">
              <input value={form.email} onChange={(e) => setField("email", e.target.value)} className={INPUT_CLASS} placeholder="customer@example.com" data-testid="form-email-input" />
            </Field>
          </div>

          {/* Address */}
          <Field label="Address line 1 *">
            <input value={form.address_line1} onChange={(e) => setField("address_line1", e.target.value)} className={INPUT_CLASS} data-testid="form-address-line1-input" />
          </Field>
          <Field label="Address line 2">
            <input value={form.address_line2} onChange={(e) => setField("address_line2", e.target.value)} className={INPUT_CLASS} data-testid="form-address-line2-input" />
          </Field>
          <div className="grid grid-cols-3 gap-2">
            <Field label="City *">
              <input value={form.city} onChange={(e) => setField("city", e.target.value)} className={INPUT_CLASS} data-testid="form-city-input" />
            </Field>
            <Field label="State *">
              <input value={form.state} onChange={(e) => setField("state", e.target.value)} className={INPUT_CLASS} data-testid="form-state-input" />
            </Field>
            <Field label="Zip *">
              <input value={form.zipcode} onChange={(e) => setField("zipcode", e.target.value)} className={INPUT_CLASS} data-testid="form-zipcode-input" />
            </Field>
          </div>
          <Field label="Country (ISO-2) *">
            <input value={form.country} onChange={(e) => setField("country", e.target.value.toUpperCase().slice(0, 2))} className={INPUT_CLASS} maxLength={2} data-testid="form-country-input" />
          </Field>

          {/* Shipping toggle */}
          <label className="flex items-center gap-2 text-[11px] font-bold text-slate-700 cursor-pointer">
            <input type="checkbox" checked={shipDifferent} onChange={(e) => setShipDifferent(e.target.checked)} data-testid="form-ship-different-toggle" />
            Ship to a different address
          </label>
          {shipDifferent && (
            <div className="space-y-2 pl-2 border-l-2 border-emerald-200">
              <div className="grid grid-cols-2 gap-2">
                <Field label="Ship first name"><input value={form.shipping_first_name} onChange={(e) => setField("shipping_first_name", e.target.value)} className={INPUT_CLASS} data-testid="form-ship-first-name" /></Field>
                <Field label="Ship last name"><input value={form.shipping_last_name} onChange={(e) => setField("shipping_last_name", e.target.value)} className={INPUT_CLASS} data-testid="form-ship-last-name" /></Field>
              </div>
              <Field label="Ship address"><input value={form.shipping_address_line1} onChange={(e) => setField("shipping_address_line1", e.target.value)} className={INPUT_CLASS} data-testid="form-ship-address" /></Field>
              <div className="grid grid-cols-3 gap-2">
                <Field label="Ship city"><input value={form.shipping_city} onChange={(e) => setField("shipping_city", e.target.value)} className={INPUT_CLASS} data-testid="form-ship-city" /></Field>
                <Field label="Ship state"><input value={form.shipping_state} onChange={(e) => setField("shipping_state", e.target.value)} className={INPUT_CLASS} data-testid="form-ship-state" /></Field>
                <Field label="Ship zip"><input value={form.shipping_zipcode} onChange={(e) => setField("shipping_zipcode", e.target.value)} className={INPUT_CLASS} data-testid="form-ship-zip" /></Field>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <Field label="Ship country"><input value={form.shipping_country} onChange={(e) => setField("shipping_country", e.target.value.toUpperCase().slice(0, 2))} maxLength={2} className={INPUT_CLASS} data-testid="form-ship-country" /></Field>
                <Field label="Ship mobile"><input value={form.shipping_mobile} onChange={(e) => setField("shipping_mobile", e.target.value)} className={INPUT_CLASS} data-testid="form-ship-mobile" /></Field>
                <Field label="Ship email"><input value={form.shipping_email} onChange={(e) => setField("shipping_email", e.target.value)} className={INPUT_CLASS} data-testid="form-ship-email" /></Field>
              </div>
            </div>
          )}

          {/* Products */}
          <div className="pt-1">
            <div className="flex items-center justify-between mb-1.5">
              <p className="text-[10px] font-extrabold uppercase tracking-wider text-slate-500">Products *</p>
              <p className="text-[9px] text-slate-400">
                Auto-fills <span className="font-bold text-slate-600">{priceFieldLabel}</span> price from catalog
                {formCurrency === "INR" ? " (INR for India orders)" : formCurrency === "USD" ? " (USD for non-India orders)" : ` (FX for ${formCurrency})`}
              </p>
            </div>
            <div className="space-y-2">
              {products.map((p, i) => {
                const lp = linePrices[i] || {};
                return (
                  <div key={`${p.itemcode || 'row'}-${i}`} className="space-y-1" data-testid={`form-product-row-${i}`}>
                    <div className="grid grid-cols-12 gap-1.5 items-end">
                      <div className="col-span-4">
                        <Field label={i === 0 ? "Itemcode" : ""}>
                          <input value={p.itemcode} onChange={(e) => updateProduct(i, "itemcode", e.target.value.toUpperCase())} className={`${INPUT_CLASS} font-mono`} placeholder="ZJ40" data-testid={`form-product-itemcode-${i}`} />
                        </Field>
                      </div>
                      <div className="col-span-2">
                        <Field label={i === 0 ? "Qty" : ""}>
                          <input type="number" min="1" step="1" value={p.qty} onChange={(e) => updateProduct(i, "qty", e.target.value)} className={INPUT_CLASS} data-testid={`form-product-qty-${i}`} />
                        </Field>
                      </div>
                      <div className="col-span-2">
                        <Field label={i === 0 ? "Size" : ""}>
                          <input value={p.size} onChange={(e) => updateProduct(i, "size", e.target.value)} className={INPUT_CLASS} placeholder="—" data-testid={`form-product-size-${i}`} />
                        </Field>
                      </div>
                      <div className="col-span-3">
                        <Field label={i === 0 ? "Color" : ""}>
                          <input value={p.color} onChange={(e) => updateProduct(i, "color", e.target.value)} className={INPUT_CLASS} placeholder="—" data-testid={`form-product-color-${i}`} />
                        </Field>
                      </div>
                      <div className="col-span-1 flex justify-center pb-1">
                        <button onClick={() => removeProduct(i)} disabled={products.length === 1}
                          className="text-rose-400 hover:text-rose-600 disabled:opacity-30 disabled:cursor-not-allowed"
                          data-testid={`form-product-remove-${i}`}>
                          <X size={13} />
                        </button>
                      </div>
                    </div>
                    {/* Iter 148 — Add-ons (pipe-separated cart_entry). */}
                    <div className="pl-1">
                      <Field label={i === 0 ? "Add-ons (optional)" : ""}>
                        <input
                          value={p.options}
                          onChange={(e) => updateProduct(i, "options", e.target.value)}
                          className={`${INPUT_CLASS} font-mono text-[10px]`}
                          placeholder="OPTIONALS_EXPRESS:_blank_:25.00 | OPTIONALS_JEWELRY_CHAINS:16 inch:12"
                          title="Pipe-separated cart_entry values from product API. Example: OPTIONALS_SCULPTURES_LACQUER:_blank_:92.4|OPTIONALS_EXPRESS:_blank_:25.00"
                          data-testid={`form-product-options-${i}`}
                        />
                      </Field>
                    </div>
                    {(p.itemcode || "").trim() && (
                      <div className="ml-1 text-[10px] flex items-center gap-1.5" data-testid={`form-product-price-${i}`}>
                        {lp.loading ? (
                          <span className="text-slate-400 italic flex items-center gap-1"><Loader2 size={9} className="animate-spin" /> Looking up price…</span>
                        ) : lp.missing ? (
                          <span className="text-rose-500 italic">⚠ No catalog match for {p.itemcode.trim().toUpperCase()}</span>
                        ) : lp.missingForCur ? (
                          <span className="text-amber-600 italic">No {priceFieldLabel} price set for this item — enter manually below</span>
                        ) : lp.unavailableForCur ? (
                          <span className="text-amber-600 italic">{priceFieldLabel} price pending (VAT) — quote via Sales</span>
                        ) : lp.unit != null ? (
                          <span className="font-mono">
                            <span className="text-slate-500">{curSymbol}{Number(lp.unit).toLocaleString("en-IN")} {priceFieldLabel}</span>
                            <span className="text-slate-400"> × {Number(p.qty) || 1} = </span>
                            <span className="font-bold text-emerald-700">{curSymbol}{Number(lp.total).toLocaleString("en-IN")}</span>
                          </span>
                        ) : null}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            <button onClick={addProduct} className="mt-1.5 text-[10px] font-bold text-emerald-700 hover:text-emerald-900 flex items-center gap-1" data-testid="form-add-product-btn">
              <Plus size={11} /> Add another product
            </button>

            {products.some((p) => (p.itemcode || "").trim()) && (
              <div className="mt-3 bg-emerald-50 border border-emerald-200 rounded-xl px-3 py-2 space-y-1" data-testid="form-grand-total">
                {discountAmount > 0 && subtotal != null && (
                  <div className="flex items-center justify-between text-[10px] text-emerald-700">
                    <span>Subtotal</span>
                    <span className="font-mono line-through opacity-70">
                      {curSymbol}{Number(subtotal).toLocaleString("en-IN")}
                    </span>
                  </div>
                )}
                {discountAmount > 0 && (
                  <div className="flex items-center justify-between text-[10px] text-violet-700" data-testid="form-discount-line">
                    <span>
                      Negotiated discount{" "}
                      {discType === "d" ? `(${discValue}%)` : discType === "c" ? `(flat)` : ""}
                    </span>
                    <span className="font-mono">
                      −{curSymbol}{Number(discountAmount).toLocaleString("en-IN", { maximumFractionDigits: 2 })}
                    </span>
                  </div>
                )}
                <div className="flex items-center justify-between">
                  <span className="text-[11px] font-bold text-emerald-900">
                    Estimated total <span className="text-[9px] text-emerald-700 font-normal">({priceFieldLabel}, before shipping/tax)</span>
                  </span>
                  <span className="text-[14px] font-extrabold text-emerald-900 font-mono" data-testid="form-grand-total-value">
                    {grandTotal == null ? "—" : `${curSymbol}${Number(grandTotal).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`}
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Optional extras */}
          {/* Yash, Apr 29 2026 — Discount block. Replaces the legacy "Coupon"
              text input. Agent picks % or $ + value; backend auto-creates a
              CRM-prefixed coupon (expiry tomorrow) and applies it before
              placing the order. The customer never sees the code. */}
          <div className="bg-violet-50 border border-violet-200 rounded-xl p-2.5 space-y-1.5" data-testid="discount-block">
            <p className="text-[10px] font-extrabold uppercase tracking-wider text-violet-700 flex items-center gap-1">
              Negotiated discount <span className="text-[8px] font-normal text-violet-500 normal-case ml-1">(auto-creates a CRM-prefixed coupon — never shown to customer)</span>
            </p>
            <div className="grid grid-cols-12 gap-1.5 items-end">
              <div className="col-span-4">
                <Field label="Type">
                  <div className="flex bg-white border border-violet-200 rounded-lg p-0.5">
                    {[
                      { val: "", lbl: "None" },
                      { val: "d", lbl: "%" },
                      { val: "c", lbl: "$" },
                    ].map((opt) => (
                      <button
                        key={opt.val || "none"}
                        type="button"
                        onClick={() => setField("auto_coupon_type", opt.val)}
                        className={`flex-1 py-1 rounded-md text-[11px] font-bold transition-all ${
                          (form.auto_coupon_type || "") === opt.val
                            ? "bg-violet-600 text-white shadow-sm"
                            : "text-violet-700 hover:bg-violet-50"
                        }`}
                        data-testid={`auto-coupon-type-${opt.val || "none"}`}
                      >
                        {opt.lbl}
                      </button>
                    ))}
                  </div>
                </Field>
              </div>
              <div className="col-span-8">
                <Field label={form.auto_coupon_type === "d" ? "Discount %" : form.auto_coupon_type === "c" ? "Flat amount" : "Value"}>
                  <input
                    type="number"
                    min="0"
                    max={form.auto_coupon_type === "d" ? "100" : undefined}
                    step={form.auto_coupon_type === "d" ? "1" : "0.01"}
                    value={form.auto_coupon_value || ""}
                    onChange={(e) => setField("auto_coupon_value", e.target.value)}
                    disabled={!form.auto_coupon_type}
                    className={`${INPUT_CLASS} ${!form.auto_coupon_type ? "opacity-50" : ""}`}
                    placeholder={form.auto_coupon_type === "d" ? "10" : form.auto_coupon_type === "c" ? "50" : "—"}
                    data-testid="auto-coupon-value-input"
                  />
                </Field>
              </div>
            </div>
          </div>

          {/* Iter 148 — COD charges + Custom reduce (flat rupees off final) */}
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-2.5 space-y-1.5" data-testid="cod-and-reduce-block">
            <p className="text-[10px] font-extrabold uppercase tracking-wider text-amber-800 flex items-center gap-1">
              COD & Custom discount
              <span className="text-[8px] font-normal text-amber-600 normal-case ml-1">(optional — integer rupees)</span>
            </p>
            <div className="grid grid-cols-2 gap-1.5">
              <Field label="COD charges (₹)">
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={form.codcharges}
                  onChange={(e) => setField("codcharges", e.target.value)}
                  className={INPUT_CLASS}
                  placeholder="0"
                  title="Cash-on-delivery surcharge added to the order total"
                  data-testid="form-codcharges"
                />
              </Field>
              <Field label="Custom discount (₹ off)">
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={form.custom_reduce}
                  onChange={(e) => setField("custom_reduce", e.target.value)}
                  className={INPUT_CLASS}
                  placeholder="0"
                  title="Flat rupees off the final total — independent of the coupon engine. Use when a specific final price has been negotiated."
                  data-testid="form-custom-reduce"
                />
              </Field>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <Field label="Or use existing coupon code">
              <input value={form.coupon} onChange={(e) => setField("coupon", e.target.value.toUpperCase())} className={`${INPUT_CLASS} font-mono`} placeholder="—" data-testid="form-coupon-input" />
            </Field>
            <Field label="Txn ID"><input value={form.transid} onChange={(e) => setField("transid", e.target.value)} className={INPUT_CLASS} placeholder="auto" data-testid="form-transid-input" /></Field>
          </div>
          <Field label="Notes / narration">
            <textarea value={form.narration} onChange={(e) => setField("narration", e.target.value)} className={`${INPUT_CLASS} min-h-[40px]`} rows={2} data-testid="form-narration-input" />
          </Field>

          {/* Submit */}
          <button
            onClick={submit}
            disabled={submitting}
            className="w-full bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white py-2.5 rounded-xl font-extrabold text-[12px] flex items-center justify-center gap-1.5 shadow-sm transition-all disabled:opacity-50"
            data-testid="form-submit-btn"
          >
            {submitting ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}
            {submitting ? "Placing order…" : "Place Admin Order"}
          </button>
        </div>
      )}
    </div>
  );
};
